import {
  PROFILE_POINTS_TARGET,
  type ScoreColor,
  VALUATION_POINTS_TARGET,
  type VisibilityScore,
  formatVisibilitySizeMultiplier,
  getScoreColor,
} from '@modules/_shared/visibility-scoring';
import type { useLocale } from '@shared/hooks/locale';

/**
 * Presentation layer for the visibility score. The numeric computation lives in
 * the pure `@realadvisor/visibility-scoring` package and runs server-side in the
 * `organisation_visibility_score` Hasura action; this file is the CRM-only
 * adapter that turns the action's numbers into localized, coloured signal rows
 * for the dashboard and popovers. It never computes a score itself.
 */

export type { ScoreColor };
export { getScoreColor };

export type VisibilityScoreSignalDetail = {
  id: 'profileViews' | 'valuationWeighted';
  label: string;
  points: number;
  maxPoints: number;
  subtitle: string;
  color: ScoreColor;
};

/** Pure score augmented with localized, coloured signal rows for the UI. */
export type VisibilityScoreResult = VisibilityScore & {
  signals: VisibilityScoreSignalDetail[];
};

/** Localized signal rows for an already-computed pure visibility score. */
export const presentVisibilityScore = ({
  score,
  t,
}: {
  score: VisibilityScore;
  t: ReturnType<typeof useLocale>['t'];
}): VisibilityScoreResult => {
  const multiplierLabel = formatVisibilitySizeMultiplier(score.sizeMultiplier);
  const effectiveDisplayPoints = Math.floor(score.effectiveImpressions);

  const profileSubtitle =
    score.sizeMultiplier === 1
      ? t('{{count}} views ({{points}} pts)', {
          count: score.profileViewsTotal,
          points: score.profilePoints,
        })
      : t(
          '{{count}} / {{target}} views ({{points}} pts · {{multiplier}} target)',
          {
            count: score.profileViewsTotal,
            target: Math.round(score.profileViewsTarget),
            points: score.profilePoints,
            multiplier: multiplierLabel,
          },
        );

  const valuationSubtitle =
    score.sizeMultiplier === 1
      ? t('{{count}} views ({{points}} pts)', {
          count: score.valuationViewsTotal,
          points: effectiveDisplayPoints,
        })
      : t(
          '{{count}} views · {{points}} effective / {{target}} target ({{multiplier}})',
          {
            count: score.valuationViewsTotal,
            points: effectiveDisplayPoints,
            target: Math.round(score.targetImpressions),
            multiplier: multiplierLabel,
          },
        );

  const signals: VisibilityScoreSignalDetail[] = [
    {
      id: 'profileViews',
      label: t('Profile views'),
      points: score.profilePoints,
      maxPoints: PROFILE_POINTS_TARGET,
      subtitle: profileSubtitle,
      color: getScoreColor(
        Math.round((score.profilePoints / PROFILE_POINTS_TARGET) * 100),
      ),
    },
    {
      id: 'valuationWeighted',
      label: t('Valuation views'),
      points: score.valuationPoints,
      maxPoints: VALUATION_POINTS_TARGET,
      subtitle: valuationSubtitle,
      color: getScoreColor(
        Math.round((score.valuationPoints / VALUATION_POINTS_TARGET) * 100),
      ),
    },
  ];

  return { ...score, signals };
};
